/**
 * NBPLCReports — Reports page for NB
 * Route: /corporate/nbplc/reports
 */
import React, { useState, useMemo, useEffect } from "react";
import { useNBPLC } from "@/context/NBPLCContext";
import { PageHeader } from "@/components/ds";
import { Download, Shield, Activity, TrendingDown, DollarSign, Leaf } from "lucide-react";
import client from "@/api/client";

const REPORTS = [
  {
    key:"corporate",
    label:"Climate & Circularity Report",
    badge:"Primary",
    badgeClass:"bg-teal-100 text-teal-700 border-teal-200",
    icon: Activity,
    desc:"16-page institutional report: Cover · TOC · Executive Summary · Climate Profile · Operational Drivers · Decarbonisation Opportunities · Carbon Value Bridge · Financing Summary · IFRS S1/S2 · TCFD · Methodology · Data & Evidence · Carbon Pathways · Disclaimers.",
    pages: "16 pages",
    endpoint: (pid) => `/nbplc/pdf/corporate/${pid}`,
    filename: (pid,pl) => `NB_Climate_${pl}.pdf`,
  },
  {
    key:"value-strategy",
    label:"Carbon Value & Strategy Report",
    badge:"Carbon",
    badgeClass:"bg-emerald-100 text-emerald-700 border-emerald-200",
    icon: TrendingDown,
    desc:"11-page strategy & capital allocation document: Opportunity rankings · Scenario analysis (Top 5 vs 10) · Carbon Value Bridge (A/B) · Strategic pathways · Capital allocation logic · Implementation roadmap · Methodology.",
    pages: "11 pages",
    endpoint: (pid) => `/nbplc/pdf/value-strategy/${pid}`,
    filename: (pid,pl) => `NB_ValueStrategy_${pl}.pdf`,
  },
  {
    key:"financing",
    label:"Financing Readiness Report",
    badge:"Finance",
    badgeClass:"bg-amber-100 text-amber-700 border-amber-200",
    icon: DollarSign,
    desc:"8-page bankable investment document: Portfolio overview · CAPEX breakdown · Operational savings (Section A, bankable) · Carbon value scenarios (Section B, conditional) · Bankability assessment · Risk factors · DFI funding structures.",
    pages: "8 pages",
    endpoint: (pid) => `/nbplc/pdf/financing/${pid}`,
    filename: (pid,pl) => `NB_Financing_${pl}.pdf`,
  },
  {
    key:"sdg",
    label:"SDG Contribution Report",
    badge:"SDG",
    badgeClass:"bg-purple-100 text-purple-700 border-purple-200",
    icon: Leaf,
    desc:"10-page impact & alignment report: SDG mapping overview · SDG 6 Water · SDG 7 Clean Energy · SDG 9 Industry · SDG 12 Packaging · SDG 13 Climate Action · Evidence-based KPI linkage · Methodology.",
    pages: "10 pages",
    endpoint: (pid) => `/nbplc/pdf/sdg/${pid}`,
    filename: (pid,pl) => `NB_SDG_${pl}.pdf`,
  },
];

export default function NBPLCReports() {
  const { selectedPeriod, period, periods, setSelectedPeriod } = useNBPLC();
  const [loading, setLoading] = useState({});
  const [errors,  setErrors]  = useState({});
  const [pageCounts, setPageCounts] = useState({});

  // Load dynamic page counts from backend
  useEffect(() => {
    if (!selectedPeriod) return;
    client.get(`/nbplc/pdf/meta/${selectedPeriod}`)
      .then(r => setPageCounts(r.data))
      .catch(() => {}); // Non-critical — fall back to "Generating..."
  }, [selectedPeriod]);

  const periodLabel = useMemo(() => {
    const p = periods.find(x => x.period_id === selectedPeriod);
    return p ? p.label.replace(" ", "") : selectedPeriod || "";
  }, [periods, selectedPeriod]);

  const handleDownload = async (report) => {
    const key = report.key;
    setLoading(prev => ({ ...prev, [key]: true }));
    setErrors(prev => ({ ...prev, [key]: null }));
    try {
      const resp = await client.get(report.endpoint(selectedPeriod), { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url; link.download = report.filename(selectedPeriod, periodLabel);
      link.click(); URL.revokeObjectURL(url);
    } catch {
      setErrors(prev => ({ ...prev, [key]: "PDF generation failed. Ensure data is calculated for this period." }));
    } finally {
      setLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">Nigeria Breweries Plc</p>
          <PageHeader title="Reports" />
          <p className="text-xs text-slate-400 mt-0.5">Institutional-grade · Brewery-specific · DCI-BREWERY-V1.0</p>
        </div>
        <select value={selectedPeriod||""} onChange={e=>setSelectedPeriod(e.target.value)}
          className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white">
          {periods.map(p=><option key={p.period_id} value={p.period_id}>{p.label}</option>)}
        </select>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 flex items-start gap-2">
        <Shield size={14} className="text-amber-400 flex-shrink-0 mt-0.5"/>
        <p className="text-xs text-amber-700">
          Validation-Ready Model Outputs · Not independently verified · DCI-BREWERY-V1.0 / IPCC 2006 / BIER
        </p>
      </div>

      <div className="space-y-4" data-testid="nb-reports-list">
        {REPORTS.map(r => (
          <div key={r.key} className="bg-white border border-slate-200 rounded-xl p-5 hover:border-teal-200 transition-colors">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 bg-slate-50 rounded-xl flex items-center justify-center flex-shrink-0 border border-slate-100">
                <r.icon size={16} className="text-teal-600"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <p className="text-sm font-semibold text-slate-900">{r.label}</p>
                  <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${r.badgeClass}`}>{r.badge}</span>
                  <span className="text-xs text-slate-400">
                    {pageCounts[r.key] ? `${pageCounts[r.key]} pages` : "—"}
                  </span>
                </div>
                <p className="text-xs text-slate-500">{r.desc}</p>
                {errors[r.key] && <p className="text-xs text-red-500 mt-1">{errors[r.key]}</p>}
              </div>
              <button onClick={() => handleDownload(r)} disabled={loading[r.key] || !selectedPeriod}
                className="flex items-center gap-1.5 px-3.5 py-2 bg-[#0F172A] text-white text-xs font-bold rounded-xl hover:bg-slate-800 disabled:opacity-50 whitespace-nowrap flex-shrink-0">
                {loading[r.key] ? <>
                  <span className="animate-spin inline-block w-3 h-3 border border-white border-t-transparent rounded-full mr-1"/>Generating...
                </> : <><Download size={12}/> Download PDF</>}
              </button>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-400 italic text-center">
        NB_Climate_[Period].pdf · NB_ValueStrategy_[Period].pdf · NB_Financing_[Period].pdf · NB_SDG_[Period].pdf
      </p>
    </div>
  );
}
